import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardActions,
  Grid,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
  LinearProgress,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Dashboard,
  SportsTennis,
  People,
  TrendingUp,
  Add,
  Menu as MenuIcon,
  Logout,
  Home,
  PersonAdd,
  Event,
  CalendarToday,
  LocationOn
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useMsal } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import { tournamentAPI } from '../services/api';

const AdminDashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  // Add CSS keyframes for animations
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes rotate {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
      @keyframes pulse {
        0%, 100% { transform: scale(1); }
        50% { transform: scale(1.05); }
      }
      @keyframes fadeInUp {
        from { opacity: 0; transform: translateY(30px); }
        to { opacity: 1; transform: translateY(0); }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);
  const { instance } = useMsal();
  
  // Local state for data
  const [tournaments, setTournaments] = useState([]);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // API functions
  const loadTournaments = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Loading tournaments...');
      const response = await tournamentAPI.getAll();
      console.log('Tournaments response:', response);
      console.log('Tournaments data:', response.data);
      // The API returns { data: [...], pageIndex: 0, totalPages: 1, ... }
      // So we need to access response.data.data for the actual tournaments array
      setTournaments(response.data?.data || []);
    } catch (error) {
      console.error('Error loading tournaments:', error);
      console.error('Error details:', error.response?.data);
      setError(`Failed to load tournaments: ${error.message}`);
      setTournaments([]);
    } finally {
      setLoading(false);
    }
  };

  const loadPlayers = async () => {
    try {
      // For now, set empty array since we don't have a players API endpoint
      setPlayers([]);
    } catch (error) {
      console.error('Error loading players:', error);
      setPlayers([]);
    }
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      await instance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin,
      });
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // Utility function to calculate current registrations for a tournament
  const getCurrentRegistrations = (tournamentId) => {
    // First try to get from players array (if available)
    const playerRegistrations = players.filter(player => player.tournamentId === tournamentId).length;
    if (playerRegistrations > 0) {
      return playerRegistrations;
    }
    
    // Fallback: get from tournament data if available
    const tournament = tournaments.find(t => t.id === tournamentId);
    return tournament?.currentRegistrations || 0;
  };
  
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);

  useEffect(() => {
    // Load data when component mounts
    loadTournaments();
    loadPlayers();
  }, []);



  const menuItems = [
    { text: 'Dashboard', icon: <Dashboard />, path: '/admin' },
    { text: 'Tournaments', icon: <SportsTennis />, path: '/admin/tournaments' },
    { text: 'Players', icon: <People />, path: '/admin/players' }
  ];

  const getTotalRegistrations = () => {
    if (!tournaments || !Array.isArray(tournaments)) {
      return 0;
    }
    return tournaments.reduce((total, tournament) => total + getCurrentRegistrations(tournament.id), 0);
  };

  const getTotalCapacity = () => {
    if (!tournaments || !Array.isArray(tournaments)) {
      return 0;
    }
    return tournaments.reduce((total, tournament) => total + tournament.maxParticipants, 0);
  };

  const getActiveTournaments = () => {
    if (!tournaments || !Array.isArray(tournaments)) {
      return 0;
    }
    return tournaments.filter(tournament => tournament.status === 'active').length;
  };


  const getTournamentStats = () => {
    console.log('getTournamentStats - tournaments:', tournaments);
    console.log('getTournamentStats - tournaments type:', typeof tournaments);
    console.log('getTournamentStats - isArray:', Array.isArray(tournaments));
    
    if (!tournaments || !Array.isArray(tournaments)) {
      console.log('getTournamentStats - returning empty array');
      return [];
    }
    
    const stats = tournaments.map(tournament => ({
      ...tournament,
      fillPercentage: Math.round((getCurrentRegistrations(tournament.id) / tournament.maxParticipants) * 100)
    }));
    
    console.log('getTournamentStats - stats:', stats);
    return stats;
  };


  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* App Bar */}
      <AppBar 
        position="fixed" 
        sx={{ 
          zIndex: theme.zIndex.drawer + 1,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
          boxShadow: '0 2px 20px rgba(0, 0, 0, 0.1)'
        }}
      >
        <Toolbar>
          <IconButton
            color="primary"
            edge="start"
            onClick={() => setDrawerOpen(!drawerOpen)}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <img
              src={require('../images/new_logo.png')}
              alt="VeiledKnights Logo"
              style={{
                width: '40px',
                height: '40px',
                marginRight: '12px',
                borderRadius: '0px'
              }}
            />
            <Typography 
              variant="h6" 
              component="div" 
              sx={{ 
                color: '#1a365d',
                fontWeight: 700,
                letterSpacing: '-0.01em'
              }}
            >
              VeiledKnights
            </Typography>
          </Box>
          <Button 
            color="primary" 
            onClick={handleLogout} 
            startIcon={<Logout />}
            sx={{
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: 2,
              px: 2,
              py: 1,
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.08)',
                transform: 'translateY(-1px)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            Logout
          </Button>
        </Toolbar>
      </AppBar>

      {/* Drawer */}
      <Drawer
        variant={isMobile ? 'temporary' : 'persistent'}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{
          width: 280,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: 280,
            boxSizing: 'border-box',
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
            backdropFilter: 'blur(20px)',
            borderRight: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          },
        }}
      >
        <Toolbar />
        <Box sx={{ overflow: 'auto', height: '100%' }}>
          {/* Admin Portal Header */}
          <Box sx={{ 
            px: 3, 
            py: 2, 
            textAlign: 'center',
            borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
            mb: 1
          }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 700,
                color: '#1a365d',
                background: 'linear-gradient(135deg, #1a365d 0%, #1976d2 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              Admin Portal
            </Typography>
          </Box>

          {/* Divider */}
          <Box sx={{ 
            mx: 2, 
            mb: 1,
            height: '2px',
            background: 'linear-gradient(90deg, transparent 0%, #1976d2 50%, transparent 100%)',
            borderRadius: '1px'
          }} />

          {/* Navigation Menu */}
          <List sx={{ px: 2 }}>
            {menuItems.map((item, index) => (
              <ListItem 
                key={item.text}
                onClick={() => {
                  navigate(item.path);
                  if (isMobile) setDrawerOpen(false);
                }}
                sx={{
                  borderRadius: 2,
                  mb: 1,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.1) 0%, rgba(25, 118, 210, 0.05) 100%)',
                    transform: 'translateX(4px)',
                    boxShadow: '0 4px 15px rgba(25, 118, 210, 0.2)'
                  }
                }}
              >
                <ListItemIcon sx={{ 
                  minWidth: 40,
                  color: '#1976d2',
                  '& .MuiSvgIcon-root': {
                    fontSize: 24
                  }
                }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.text}
                  sx={{
                    '& .MuiTypography-root': {
                      fontWeight: 600,
                      color: '#1a365d',
                      fontSize: '0.95rem'
                    }
                  }}
                />
              </ListItem>
            ))}
          </List>

          <Divider sx={{ mx: 2, my: 2, borderColor: 'rgba(255, 255, 255, 0.2)' }} />

          {/* Public Site Link */}
          <List sx={{ px: 2 }}>
            <ListItem 
              onClick={() => navigate('/')}
              sx={{
                borderRadius: 2,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.1) 0%, rgba(25, 118, 210, 0.05) 100%)',
                  transform: 'translateX(4px)',
                  boxShadow: '0 4px 15px rgba(25, 118, 210, 0.2)'
                }
              }}
            >
              <ListItemIcon sx={{ 
                minWidth: 40,
                color: '#1976d2',
                '& .MuiSvgIcon-root': {
                  fontSize: 24
                }
              }}>
                <Home />
              </ListItemIcon>
              <ListItemText 
                primary="Public Site"
                sx={{
                  '& .MuiTypography-root': {
                    fontWeight: 600,
                    color: '#1a365d',
                    fontSize: '0.95rem'
                  }
                }}
              />
            </ListItem>
          </List>
        </Box>
      </Drawer>

      {/* Main Content */}
      <Box 
        component="main" 
        sx={{ 
          flexGrow: 1, 
          p: 3, 
          mt: 8,
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          minHeight: 'calc(100vh - 64px)'
        }}
      >
        <Container maxWidth="xl">
          {/* Welcome Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Box sx={{ 
              textAlign: 'center', 
              mb: 6,
              background: 'rgba(255, 255, 255, 0.7)',
              borderRadius: 3,
              p: 4,
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
            }}>
              <Typography 
                variant="h3" 
                component="h1" 
                sx={{ 
                  fontWeight: 800, 
                  mb: 2,
                  background: 'linear-gradient(135deg, #1a365d 0%, #1976d2 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  letterSpacing: '-0.02em'
                }}
              >
                Admin Dashboard
              </Typography>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: '#4a5568',
                  fontWeight: 400,
                  maxWidth: '600px',
                  mx: 'auto'
                }}
              >
                Manage tournaments, players, and oversee the VeiledKnights community
              </Typography>
            </Box>
          </motion.div>

          {/* Stats Cards */}
          <Box sx={{ width: '95%', mx: 'auto', mb: 4 }}>
            <Grid container spacing={3} sx={{ justifyContent: 'center' }}>
              <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', justifyContent: 'center' }}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  style={{ width: '100%', maxWidth: '280px' }}
                >
                  <Card sx={{ 
                    height: '180px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    width: '100%',
                    borderRadius: 3,
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    background: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
                      borderColor: '#1976d2'
                    }
                  }}>
                    <CardContent sx={{ width: '100%', overflow: 'hidden', p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Box sx={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 2,
                          flexShrink: 0,
                          boxShadow: '0 4px 15px rgba(25, 118, 210, 0.3)'
                        }}>
                          <SportsTennis sx={{ color: 'white', fontSize: 24 }} />
                        </Box>
                        <Typography variant="h6" component="div" sx={{ 
                          whiteSpace: 'nowrap', 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis', 
                          flex: 1,
                          fontWeight: 600,
                          color: '#1a365d'
                        }}>
                          Active Tournaments
                        </Typography>
                      </Box>
                      <Typography variant="h3" component="div" sx={{ 
                        fontWeight: 800, 
                        mb: 1,
                        background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent'
                      }}>
                        {getActiveTournaments()}
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        whiteSpace: 'nowrap', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis',
                        color: '#4a5568',
                        fontWeight: 500
                      }}>
                        out of {tournaments.length} total
                      </Typography>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>

              <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', justifyContent: 'center' }}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  style={{ width: '100%', maxWidth: '280px' }}
                >
                  <Card sx={{ 
                    height: '180px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    width: '100%',
                    borderRadius: 3,
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    background: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
                      borderColor: '#1976d2'
                    }
                  }}>
                    <CardContent sx={{ width: '100%', overflow: 'hidden', p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Box sx={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 2,
                          flexShrink: 0,
                          boxShadow: '0 4px 15px rgba(25, 118, 210, 0.3)'
                        }}>
                          <People sx={{ color: 'white', fontSize: 24 }} />
                        </Box>
                        <Typography variant="h6" component="div" sx={{ 
                          whiteSpace: 'nowrap', 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis', 
                          flex: 1,
                          fontWeight: 600,
                          color: '#1a365d'
                        }}>
                          Total Players
                        </Typography>
                      </Box>
                      <Typography variant="h3" component="div" sx={{ 
                        fontWeight: 800, 
                        mb: 1,
                        background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent'
                      }}>
                        {players.length}
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        whiteSpace: 'nowrap', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis',
                        color: '#4a5568',
                        fontWeight: 500
                      }}>
                        registered across all tournaments
                      </Typography>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>

              <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', justifyContent: 'center' }}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  style={{ width: '100%', maxWidth: '280px' }}
                >
                  <Card sx={{ 
                    height: '180px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    width: '100%',
                    borderRadius: 3,
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    background: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
                      borderColor: '#1976d2'
                    }
                  }}>
                    <CardContent sx={{ width: '100%', overflow: 'hidden', p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Box sx={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 2,
                          flexShrink: 0,
                          boxShadow: '0 4px 15px rgba(25, 118, 210, 0.3)'
                        }}>
                          <TrendingUp sx={{ color: 'white', fontSize: 24 }} />
                        </Box>
                        <Typography variant="h6" component="div" sx={{ 
                          whiteSpace: 'nowrap', 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis', 
                          flex: 1,
                          fontWeight: 600,
                          color: '#1a365d'
                        }}>
                          Fill Rate
                        </Typography>
                      </Box>
                      <Typography variant="h3" component="div" sx={{ 
                        fontWeight: 800, 
                        mb: 1,
                        background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent'
                      }}>
                        {getTotalCapacity() > 0 ? Math.round((getTotalRegistrations() / getTotalCapacity()) * 100) : 0}%
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        whiteSpace: 'nowrap', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis',
                        color: '#4a5568',
                        fontWeight: 500
                      }}>
                        {getTotalRegistrations()}/{getTotalCapacity()} registrations
                      </Typography>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>

              <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', justifyContent: 'center' }}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                  style={{ width: '100%', maxWidth: '280px' }}
                >
                  <Card sx={{ 
                    height: '180px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    width: '100%',
                    borderRadius: 3,
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    background: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
                      borderColor: '#1976d2'
                    }
                  }}>
                    <CardContent sx={{ width: '100%', overflow: 'hidden', p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Box sx={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 2,
                          flexShrink: 0,
                          boxShadow: '0 4px 15px rgba(25, 118, 210, 0.3)'
                        }}>
                          <Event sx={{ color: 'white', fontSize: 24 }} />
                        </Box>
                        <Typography variant="h6" component="div" sx={{ 
                          whiteSpace: 'nowrap', 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis', 
                          flex: 1,
                          fontWeight: 600,
                          color: '#1a365d'
                        }}>
                          Upcoming Events
                        </Typography>
                      </Box>
                      <Typography variant="h3" component="div" sx={{ 
                        fontWeight: 800, 
                        mb: 1,
                        background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent'
                      }}>
                        {tournaments && Array.isArray(tournaments) ? tournaments.filter(t => t.startDate > Date.now()).length : 0}
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        whiteSpace: 'nowrap', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis',
                        color: '#4a5568',
                        fontWeight: 500
                      }}>
                        tournaments starting soon
                      </Typography>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            </Grid>
          </Box>

          {/* Quick Actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <Box sx={{ width: '95%', mx: 'auto', mb: 4 }}>
              <Card sx={{
                borderRadius: 3,
                border: '1px solid rgba(255, 255, 255, 0.2)',
                background: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                overflow: 'hidden'
              }}>
                <CardContent sx={{ p: 4 }}>
                  <Typography 
                    variant="h5" 
                    component="h2" 
                    gutterBottom 
                    sx={{ 
                      fontWeight: 700,
                      color: '#1a365d',
                      mb: 3,
                      textAlign: 'center'
                    }}
                  >
                    Quick Actions
                  </Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    gap: 3, 
                    flexWrap: 'wrap',
                    justifyContent: 'center'
                  }}>
                    <Button
                      variant="contained"
                      startIcon={<Add />}
                      onClick={() => navigate('/admin/tournaments')}
                      sx={{
                        px: 4,
                        py: 1.5,
                        fontSize: '1rem',
                        fontWeight: 600,
                        borderRadius: 2,
                        textTransform: 'none',
                        background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                        boxShadow: '0 4px 15px rgba(25, 118, 210, 0.3)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                          boxShadow: '0 6px 20px rgba(25, 118, 210, 0.4)',
                          transform: 'translateY(-2px)'
                        },
                        transition: 'all 0.3s ease'
                      }}
                    >
                      Create Tournament
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<PersonAdd />}
                      onClick={() => navigate('/admin/players')}
                      sx={{
                        px: 4,
                        py: 1.5,
                        fontSize: '1rem',
                        fontWeight: 600,
                        borderRadius: 2,
                        textTransform: 'none',
                        borderColor: '#1976d2',
                        color: '#1976d2',
                        borderWidth: 2,
                        '&:hover': {
                          borderColor: '#1565c0',
                          backgroundColor: 'rgba(25, 118, 210, 0.08)',
                          transform: 'translateY(-2px)'
                        },
                        transition: 'all 0.3s ease'
                      }}
                    >
                      Add Player
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </motion.div>

          {/* Tournament Overview */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <Box sx={{ width: '95%', mx: 'auto' }}>
              {/* Tournament Cards Container */}
              <Box sx={{ 
                background: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(10px)',
                borderRadius: 3,
                border: '1px solid rgba(255, 255, 255, 0.2)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                p: { xs: 3, sm: 4, md: 5 },
                mb: 4,
                overflow: 'hidden'
              }}>
                <Typography 
                  variant="h5" 
                  component="h2" 
                  gutterBottom 
                  sx={{ 
                    fontWeight: 700, 
                    mb: 4, 
                    textAlign: 'center',
                    color: '#1a365d',
                    background: 'linear-gradient(135deg, #1a365d 0%, #1976d2 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}
                >
                  Tournament Overview
                </Typography>
                <Grid container spacing={3} sx={{ alignItems: 'stretch', justifyContent: 'center' }}>
                  {loading ? (
                    <Grid item xs={12} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="h6">Loading tournaments...</Typography>
                    </Grid>
                  ) : error ? (
                    <Grid item xs={12} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="h6" color="error">
                        {error}
                      </Typography>
                    </Grid>
                  ) : getTournamentStats().length > 0 ? (
                    getTournamentStats().map((tournament, index) => (
                    <Grid item xs={12} sm={6} md={6} key={tournament.id} sx={{ display: 'flex', justifyContent: 'center' }}>
                      <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: index * 0.1 }}
                        style={{ width: '100%', maxWidth: '320px' }}
                      >
                        <Card 
                          sx={{ 
                            height: '520px', 
                            width: '100%',
                            minWidth: '280px',
                            maxWidth: '320px',
                            display: 'flex', 
                            flexDirection: 'column',
                            borderRadius: 3,
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            background: 'rgba(255, 255, 255, 0.95)',
                            backdropFilter: 'blur(10px)',
                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              transform: 'translateY(-8px)',
                              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
                              borderColor: '#1976d2'
                            }
                          }}
                        >
                          <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', height: '100%', p: 3 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                              <Typography 
                                variant="h6" 
                                component="h3" 
                                sx={{ 
                                  fontWeight: 700, 
                                  fontSize: '1.1rem', 
                                  lineHeight: 1.3,
                                  color: '#1a365d'
                                }}
                              >
                                {tournament.name}
                              </Typography>
                              <Chip 
                                label={tournament.sport} 
                                sx={{ 
                                  fontWeight: 600,
                                  background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                                  color: 'white',
                                  border: 'none',
                                  boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)'
                                }}
                                size="small"
                              />
                            </Box>
                            
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                mb: 2, 
                                fontSize: '0.875rem', 
                                lineHeight: 1.4, 
                                minHeight: '40px',
                                color: '#4a5568',
                                fontWeight: 500
                              }}
                            >
                              {tournament.description || `Join us for an exciting ${tournament.sport} tournament!`}
                            </Typography>

                            <Box sx={{ mb: 2 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <CalendarToday sx={{ mr: 1, fontSize: 16, color: '#1976d2' }} />
                                <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#4a5568', fontWeight: 500 }}>
                                  {new Date(tournament.startDate).toLocaleDateString()} - {new Date(tournament.endDate).toLocaleDateString()}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <LocationOn sx={{ mr: 1, fontSize: 16, color: '#1976d2' }} />
                                <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#4a5568', fontWeight: 500 }}>{tournament.location}</Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <People sx={{ mr: 1, fontSize: 16, color: '#1976d2' }} />
                                <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#4a5568', fontWeight: 500 }}>
                                  {getCurrentRegistrations(tournament.id)}/{tournament.maxParticipants} participants
                                </Typography>
                              </Box>
                            </Box>

                            <Box sx={{ mb: 2 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body2" sx={{ color: '#4a5568', fontWeight: 600 }}>
                                  Registration Progress
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#1976d2', fontWeight: 700 }}>
                                  {tournament.fillPercentage}%
                                </Typography>
                              </Box>
                              <LinearProgress 
                                variant="determinate" 
                                value={tournament.fillPercentage} 
                                sx={{ 
                                  height: 8, 
                                  borderRadius: 4,
                                  backgroundColor: 'rgba(25, 118, 210, 0.1)',
                                  '& .MuiLinearProgress-bar': {
                                    background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                                    borderRadius: 4
                                  }
                                }}
                              />
                            </Box>

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                              <Typography variant="body2" sx={{ color: '#4a5568', fontWeight: 500 }}>
                                {tournament.status === 'active' ? 'Active Tournament' : 'Inactive Tournament'}
                              </Typography>
                              <Chip 
                                label={tournament.status === 'active' ? 'Active' : 'Inactive'} 
                                sx={{
                                  fontWeight: 600,
                                  background: tournament.status === 'active' 
                                    ? 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)'
                                    : 'linear-gradient(135deg, #9e9e9e 0%, #bdbdbd 100%)',
                                  color: 'white',
                                  border: 'none',
                                  boxShadow: tournament.status === 'active' 
                                    ? '0 2px 8px rgba(76, 175, 80, 0.3)'
                                    : '0 2px 8px rgba(158, 158, 158, 0.3)'
                                }}
                                size="small"
                              />
                            </Box>
                            
                            {/* Spacer to push button to bottom */}
                            <Box sx={{ flexGrow: 1 }} />
                          </CardContent>
                          
                          <CardActions sx={{ p: 3, pt: 0, mt: 'auto' }}>
                            <Button 
                              fullWidth 
                              variant="contained"
                              onClick={() => navigate('/admin/tournaments')}
                              sx={{ 
                                height: '48px',
                                fontSize: '1rem',
                                fontWeight: 600,
                                textTransform: 'none',
                                borderRadius: 2,
                                background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                                boxShadow: '0 4px 15px rgba(25, 118, 210, 0.3)',
                                '&:hover': {
                                  background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                                  boxShadow: '0 6px 20px rgba(25, 118, 210, 0.4)',
                                  transform: 'translateY(-1px)'
                                },
                                transition: 'all 0.3s ease'
                              }}
                            >
                              Manage Tournament
                            </Button>
                          </CardActions>
                        </Card>
                      </motion.div>
                    </Grid>
                    ))
                  ) : (
                    <Grid item xs={12} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="h6">No tournaments available at the moment.</Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>
            </Box>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
};

export default AdminDashboard;
